import {Box, createStyles, Kbd, Paper, Stack, Text} from '@mantine/core';
import React, {useCallback, useLayoutEffect, useState} from 'react';

import useResizeObserver from 'ui-library/hooks/useResizeObserver';

import {PendingCut} from '../EditorTypes';

const useOverlayStyles = createStyles((theme) => ({
  overlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    zIndex: 8,
    pointerEvents: 'auto',
    cursor: 'default',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    outline: `2px dashed ${theme.colors.blue[6]}`,
    outlineOffset: -2,
    borderRadius: theme.radius.sm,
    backgroundColor: theme.fn.rgba(theme.colors.blue[0], 0.8),
  },
  overlayCard: {
    borderColor: theme.colors.blue[4],
  },
}));

function measureBounds(
  container: HTMLElement,
  pendingCuts: Array<PendingCut>
): {top: number; height: number} | null {
  const firstPendingCut = container.querySelector(`[data-scroll-id="${pendingCuts[0].cid}"]`);
  const lastPendingCut = container.querySelector(
    `[data-scroll-id="${pendingCuts[pendingCuts.length - 1].cid}"]`
  );

  if (firstPendingCut == null || lastPendingCut == null) {
    return null;
  }

  const containerTop = container.getBoundingClientRect().top;
  const top = firstPendingCut.getBoundingClientRect().top - containerTop;
  const bottom = lastPendingCut.getBoundingClientRect().bottom - containerTop;

  return {top, height: bottom - top};
}

/**
 * Overlay rendered at the Editor level that spans all pending-cut items.
 * Locates items via data-scroll-id selectors on the editor container element.
 */
export function PendingCutOverlay({
  pendingCuts,
  editorRef,
}: {
  pendingCuts: Array<PendingCut>;
  editorRef: HTMLDivElement | null;
}): JSX.Element {
  const {classes} = useOverlayStyles();
  const [bounds, setBounds] = useState<{top: number; height: number} | null>(null);
  const {width, height} = useResizeObserver<HTMLDivElement>({ref: editorRef});

  const measure = useCallback(() => {
    if (editorRef == null) {
      setBounds(null);
      return;
    }
    setBounds(measureBounds(editorRef, pendingCuts));
  }, [editorRef, pendingCuts]);

  useLayoutEffect(() => {
    measure();
  }, [measure, width, height]);

  return (
    <Box className={classes.overlay} style={bounds != null ? bounds : {display: 'none'}}>
      <Paper shadow="sm" radius="md" p="sm" px="lg" withBorder={true} className={classes.overlayCard}>
        <Stack spacing={2} align="center">
          <Text size="sm" weight={600} color="gray.7">
            Paste to move content
          </Text>
          <Text size="sm" color="dimmed">
            <Kbd size="sm" sx={{verticalAlign: 'middle'}}>
              Esc
            </Kbd>{' '}
            to cancel
          </Text>
        </Stack>
      </Paper>
    </Box>
  );
}
